// Celular → E.164 (#1401, #1590). Espejo de backend/app/user_api/phone.py: el
// backend es el que decide y guarda; esto solo sirve para avisar ANTES de
// enviar y no hacer esperar un 400. Si se cambia la regla (o la lista de
// países), cambiarla en los dos lados.

export const PHONE_INVALID_MESSAGE =
  "Ingresa los 8 dígitos de tu celular (ej. 1234 5678).";

export const PHONE_INVALID_MESSAGE_EXTRANJERO =
  "Ingresa tu celular sin el código del país (entre 6 y 12 dígitos).";

// #1590: ISO alpha-2 → código telefónico y nombre. Lista CERRADA con las mismas
// claves y códigos que `PAISES` del backend (el orden es propio del front:
// Chile primero, que es el default, y después alfabético por nombre).
export const PAISES = [
  { iso: "CL", code: "56", nombre: "Chile" },
  { iso: "DE", code: "49", nombre: "Alemania" },
  { iso: "AR", code: "54", nombre: "Argentina" },
  { iso: "BO", code: "591", nombre: "Bolivia" },
  { iso: "BR", code: "55", nombre: "Brasil" },
  { iso: "CA", code: "1", nombre: "Canadá" },
  { iso: "CO", code: "57", nombre: "Colombia" },
  { iso: "CR", code: "506", nombre: "Costa Rica" },
  { iso: "CU", code: "53", nombre: "Cuba" },
  { iso: "EC", code: "593", nombre: "Ecuador" },
  { iso: "SV", code: "503", nombre: "El Salvador" },
  { iso: "ES", code: "34", nombre: "España" },
  { iso: "US", code: "1", nombre: "Estados Unidos" },
  { iso: "FR", code: "33", nombre: "Francia" },
  { iso: "GT", code: "502", nombre: "Guatemala" },
  { iso: "HT", code: "509", nombre: "Haití" },
  { iso: "HN", code: "504", nombre: "Honduras" },
  { iso: "IT", code: "39", nombre: "Italia" },
  { iso: "MX", code: "52", nombre: "México" },
  { iso: "NI", code: "505", nombre: "Nicaragua" },
  { iso: "PA", code: "507", nombre: "Panamá" },
  { iso: "PY", code: "595", nombre: "Paraguay" },
  { iso: "PE", code: "51", nombre: "Perú" },
  { iso: "PT", code: "351", nombre: "Portugal" },
  { iso: "PR", code: "1", nombre: "Puerto Rico" },
  { iso: "GB", code: "44", nombre: "Reino Unido" },
  { iso: "DO", code: "1", nombre: "República Dominicana" },
  { iso: "UY", code: "598", nombre: "Uruguay" },
  { iso: "VE", code: "58", nombre: "Venezuela" },
];

// Celular ya guardado en forma canónica: `+569` + 8 dígitos (Chile) o E.164 de
// otro país (7 a 15 dígitos; ningún código que no sea Chile empieza con 56).
// Espejo de `CANONICAL_RE` de backend/app/user_api/phone.py, que es el que
// decide `phone_required`. Lo usa /admin/users para el botón de WhatsApp.
export const CANONICAL_RE = /^(\+569\d{8}|\+(?!56)[1-9]\d{6,14})$/;

const PAIS_POR_ISO = Object.fromEntries(PAISES.map((p) => [p.iso, p]));

export const paisPorIso = (iso) => PAIS_POR_ISO[(iso || "CL").toUpperCase()] || PAIS_POR_ISO.CL;

// Bandera emoji desde el ISO (indicadores regionales). En Windows se ve como
// las dos letras, por eso siempre va acompañada del nombre del país.
export const banderaPais = (iso) =>
  String(iso || "")
    .toUpperCase()
    .replace(/[A-Z]/g, (c) => String.fromCodePoint(0x1f1a5 + c.charCodeAt(0)));

// Lo que se muestra fijo a la izquierda del campo: en Chile el "+56 9" de
// #1401 (el médico escribe solo los 8 dígitos que siguen); fuera, el código.
export const prefijoCelular = (iso) => {
  const p = paisPorIso(iso);
  return p.iso === "CL" ? "+56 9" : `+${p.code}`;
};

export const placeholderCelular = (iso) => (paisPorIso(iso).iso === "CL" ? "1234 5678" : "Tu número");

export const mensajeCelularInvalido = (iso) =>
  paisPorIso(iso).iso === "CL" ? PHONE_INVALID_MESSAGE : PHONE_INVALID_MESSAGE_EXTRANJERO;

// Ronda 2 (#1401, revisor): el campo del registro muestra un prefijo FIJO
// "+56 9" y solo pide los 8 dígitos que siguen (ver AuthenticationForm.js).
// Sin este caso, escribir justo lo que la UI pedía (8 dígitos) se rechazaba
// como "número a medias" (phone8.png: submit deshabilitado siempre).
export const normalizeClMobile = (raw) => {
  let digits = String(raw ?? "").replace(/\D/g, "");
  if (digits.startsWith("56") && digits.length === 11) digits = digits.slice(2);
  else if (digits.startsWith("0") && digits.length === 10) digits = digits.slice(1);
  else if (digits.length === 8) digits = `9${digits}`;
  if (digits.length !== 9 || !digits.startsWith("9")) return null;
  return `+56${digits}`;
};

// #1590: celular en E.164 según el país, o null. Mismas reglas que
// `normalize_mobile` del backend: sin país = Chile; fuera de Chile el código
// solo se saca si viene marcado con "+" o "00", después un 0 troncal, y quedan
// entre 6 y 12 dígitos.
export const normalizeMobile = (raw, iso = "CL") => {
  const pais = PAIS_POR_ISO[(iso || "CL").toUpperCase()];
  if (!pais) return null;
  if (pais.iso === "CL") return normalizeClMobile(raw);
  let texto = String(raw ?? "").trim();
  let digits = texto.replace(/\D/g, "");
  if (texto.startsWith("00")) {
    digits = digits.slice(2);
    texto = "+";
  }
  if (texto.startsWith("+")) {
    if (!digits.startsWith(pais.code)) return null;
    digits = digits.slice(pais.code.length);
  }
  if (digits.startsWith("0")) digits = digits.slice(1);
  if (digits.length < 6 || digits.length > 12 || pais.code.length + digits.length > 15) return null;
  return `+${pais.code}${digits}`;
};

// Para precargar un celular guardado en el campo (que muestra el prefijo
// aparte): devuelve el país deducido y lo que va en el input. Un número que
// no calza con ningún país conocido se devuelve vacío, como hacía
// `soloLosOchoDigitos` en ProfileRequiredGate (un fijo viejo no se convierte
// en un celular inventado).
export const separarCelularGuardado = (phone, isoGuardado) => {
  const canon = String(phone || "").trim();
  const ma = /^\+569(\d{8})$/.exec(canon);
  if (ma) return { iso: "CL", local: ma[1] };
  const iso = (isoGuardado || "").toUpperCase();
  const pais = PAIS_POR_ISO[iso];
  if (pais && pais.iso !== "CL" && canon.startsWith(`+${pais.code}`)) {
    return { iso: pais.iso, local: canon.slice(pais.code.length + 1) };
  }
  return { iso: iso && PAIS_POR_ISO[iso] ? iso : "CL", local: "" };
};
